"use client";

import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { RefreshCw } from "lucide-react";

/** A dialog has its own scroll position; the page behind it must not block its gesture. */
export function canPullToRefresh(target: Element): boolean {
  if (target.closest('input, textarea, [contenteditable="true"], [role="slider"], [data-no-pull-refresh]')) return false;
  for (let el: Element | null = target; el; el = el.parentElement) {
    if (el instanceof HTMLElement && el.scrollHeight > el.clientHeight && /auto|scroll/.test(getComputedStyle(el).overflowY) && el.scrollTop > 0) return false;
    if (el.getAttribute("role") === "dialog") return true;
  }
  return (document.scrollingElement?.scrollTop ?? window.scrollY) <= 0;
}

export function PagePullToRefresh() {
  const qc = useQueryClient();
  const router = useRouter();
  const t = useTranslations("Network");
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const refreshing = useRef(false);
  useEffect(() => {
    let start: { x: number; y: number; target: Element } | null = null;
    let distance = 0;
    let active = true;
    const reset = () => { start = null; distance = 0; if (active) setPull(0); };
    const begin = (event: TouchEvent) => {
      reset();
      const touch = event.touches[0];
      if (refreshing.current || event.touches.length !== 1 || !touch || !(event.target instanceof Element) || !canPullToRefresh(event.target)) return;
      start = { x: touch.clientX, y: touch.clientY, target: event.target };
    };
    const move = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!start || !touch) return;
      const dy = touch.clientY - start.y;
      if (event.touches.length !== 1 || dy < 0 || Math.abs(touch.clientX - start.x) > Math.abs(dy) || !canPullToRefresh(start.target)) return reset();
      if (dy < 8) return;
      if (event.cancelable) event.preventDefault();
      distance = Math.min(dy * 0.5, 96);
      setPull(distance);
    };
    const end = () => {
      if (!start || distance < 64) return reset();
      start = null;
      refreshing.current = true;
      setBusy(true);
      setPull(64);
      // Revalidate data without losing open dialogs, the crop or unsent composer text.
      void qc.refetchQueries({ type: "active" }).then(() => router.refresh()).catch(() => undefined).finally(() => {
        refreshing.current = false;
        if (active) { setBusy(false); reset(); }
      });
    };
    document.addEventListener("touchstart", begin, { passive: true });
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("touchend", end);
    document.addEventListener("touchcancel", reset);
    return () => {
      active = false;
      document.removeEventListener("touchstart", begin);
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", end);
      document.removeEventListener("touchcancel", reset);
    };
  }, [qc, router]);
  if (!pull && !busy) return null;
  return (
    <div role="status" aria-label={t("refresh")} className="pointer-events-none fixed inset-x-0 top-[max(12px,env(safe-area-inset-top))] z-[100] flex justify-center" style={{ transform: `translateY(${Math.min(pull, 64)}px)` }}>
      <span className="flex size-10 items-center justify-center rounded-full border border-border bg-background text-primary shadow-lg">
        <RefreshCw aria-hidden className={busy ? "size-5 animate-spin motion-reduce:animate-none" : "size-5"} style={busy ? undefined : { transform: `rotate(${pull * 4}deg)` }} />
      </span>
    </div>
  );
}
